//src/hooks/useBotSettings.ts
// src/hooks/useBotSettings.ts
import { useEffect, useState, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabaseClient'
import { toStrategyKey } from '@/lib/strategyKeys'

export type BotForm = {
  id: string | null
  [k: string]: any
}

const globalFormCache: Record<string, BotForm> = {};

function parseJsonArray(value: any): any[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try { 
      let parsed = JSON.parse(value); 
      if (typeof parsed === 'string') parsed = JSON.parse(parsed); // Sustabdo dvigubo teksto klaidą
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }
  return [];
}

function parseBoolean(value: any, defaultValue: boolean): boolean {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return defaultValue; 
}

export function normalizeBotToForm(data: any): BotForm {
  if (!data) return {} as BotForm;
  const normalizedKey = toStrategyKey(data.strategy_key ?? data.strategy) ?? data.strategy;
  
  let settingsObj: any = {};
  try { settingsObj = typeof data.settings === 'string' ? JSON.parse(data.settings) : (data.settings || {}); } catch(e) {}
  
  let settingsJson: any = {};
  try { settingsJson = typeof data.settings_json === 'string' ? JSON.parse(data.settings_json) : (data.settings_json || {}); } catch(e) {}

  const merged = { ...settingsObj, ...settingsJson };

  const isLong = parseBoolean(data.long_entry_enabled ?? data.long_enabled ?? merged.longEnabled ?? merged.longEntryEnabled, true);
  const isShort = parseBoolean(data.short_entry_enabled ?? data.short_enabled ?? merged.shortEnabled ?? merged.shortEntryEnabled, true);

  return {
    ...data,
    ...merged,
    id: data.id,
    name: data.name ?? '',
    description: data.description ?? '',
    exchange: data.exchange ?? '',
    strategy: normalizedKey ?? '',
    runMode: data.is_practice ? 'paper' : 'live',
    deploymentTarget: data.deployment_target ?? (data.is_practice ? 'paper' : 'live'),
    isPractice: data.is_practice !== false,
    
    longEnabled: isLong,
    longEntryEnabled: isLong,
    long_entry_enabled: isLong,
    
    shortEnabled: isShort,
    shortEntryEnabled: isShort,
    short_entry_enabled: isShort,

    quoteCurrency: data.quote_currency ?? merged.quoteCurrency ?? '',
    accountBalance: data.account_balance ?? data.initial_capital ?? merged.accountBalance ?? '',
    percentageAmount: data.percentage_amount ?? merged.percentageAmount ?? '',
    minAmount: data.min_amount ?? merged.minAmount ?? '',
    maxAllocation: data.max_allocation ?? merged.maxAllocation ?? '',
    useFixedAmount: parseBoolean(data.use_fixed_amount ?? merged.useFixedAmount, false),

    longOrderType: data.long_order_type ?? data.buy_order_type ?? merged.buyOrderType ?? '',
    buyOrderType: data.buy_order_type ?? data.long_order_type ?? merged.buyOrderType ?? '',
    maxOpenPositions: data.max_open_positions ?? merged.maxOpenPositions ?? '',
    onlyOneOpenBuyOrder: parseBoolean(data.only_one_open_buy_order ?? merged.onlyOneOpenBuyOrder, false),
    buyPercentRange: data.buy_percent_range ?? merged.buyPercentRange ?? '',
    buyCooldownTime: data.buy_cooldown_time ?? merged.buyCooldownTime ?? '',
    
    shortOrderType: data.short_order_type ?? data.sell_order_type ?? merged.sellOrderType ?? '',
    sellOrderType: data.sell_order_type ?? data.short_order_type ?? merged.sellOrderType ?? '',
    maxOpenOrders: data.max_open_orders ?? merged.maxOpenOrders ?? '',
    onlyOneOpenSellOrder: parseBoolean(data.only_one_open_sell_order ?? merged.onlyOneOpenSellOrder, false),
    onlyNegativePairs: parseBoolean(data.only_negative_pairs ?? merged.onlyNegativePairs, false),
    sellPercentRange: data.sell_percent_range ?? merged.sellPercentRange ?? '',
    sellCooldownTime: data.sell_cooldown_time ?? merged.sellCooldownTime ?? '',

    selectedCoins: parseJsonArray(data.selected_coins ?? merged.selectedCoins),
    primaryTimeInterval: data.primary_time_interval ?? data.timeframe_primary ?? merged.primaryTimeInterval ?? '',
    primaryTimeIndicators: parseJsonArray(data.primary_time_indicators ?? merged.primaryTimeIndicators),
    secondaryTimeInterval: data.secondary_time_interval ?? data.timeframe_secondary ?? merged.secondaryTimeInterval ?? '',
    secondaryTimeIndicators: parseJsonArray(data.secondary_time_indicators ?? merged.secondaryTimeIndicators),

    buyTakeProfitStagesEnabled: parseBoolean(data.buy_take_profit_stages_enabled ?? merged.buyTakeProfitStagesEnabled, false),
    buyTakeProfitStagesUseFixedAmount: parseBoolean(data.buy_take_profit_stages_use_fixed_amount ?? merged.buyTakeProfitStagesUseFixedAmount, false),
    buyTakeProfitStagesData: parseJsonArray(data.buy_take_profit_stages_data ?? merged.buyTakeProfitStagesData),
    buyTakeProfitStagesInitialTrailingStop: data.buy_take_profit_stages_initial_trailing_stop ?? merged.buyTakeProfitStagesInitialTrailingStop ?? '',
    buyTakeProfitStagesDynamicTrailingEnabled: parseBoolean(data.buy_take_profit_stages_dynamic_trailing_enabled ?? merged.buyTakeProfitStagesDynamicTrailingEnabled, false),
    buyTakeProfitStagesAutoAdjustEnabled: parseBoolean(data.buy_take_profit_stages_auto_adjust_enabled ?? merged.buyTakeProfitStagesAutoAdjustEnabled, false),

    sellTakeProfitStagesEnabled: parseBoolean(data.sell_take_profit_stages_enabled ?? merged.sellTakeProfitStagesEnabled, false),
    sellTakeProfitStagesUseFixedAmount: parseBoolean(data.sell_take_profit_stages_use_fixed_amount ?? merged.sellTakeProfitStagesUseFixedAmount, false),
    sellTakeProfitStagesData: parseJsonArray(data.sell_take_profit_stages_data ?? merged.sellTakeProfitStagesData),
    sellTakeProfitStagesInitialTrailingStop: data.sell_take_profit_stages_initial_trailing_stop ?? merged.sellTakeProfitStagesInitialTrailingStop ?? '',
    sellTakeProfitStagesDynamicTrailingEnabled: parseBoolean(data.sell_take_profit_stages_dynamic_trailing_enabled ?? merged.sellTakeProfitStagesDynamicTrailingEnabled, false),
    sellTakeProfitStagesAutoAdjustEnabled: parseBoolean(data.sell_take_profit_stages_auto_adjust_enabled ?? merged.sellTakeProfitStagesAutoAdjustEnabled, false),

    buyTrailingEntryEnabled: parseBoolean(data.buy_trailing_entry_enabled ?? merged.buyTrailingEntryEnabled, false),
    buyTrailingEntryStages: parseJsonArray(data.buy_trailing_entry_stages ?? merged.buyTrailingEntryStages),
    
    sellTrailingExitEnabled: parseBoolean(data.sell_trailing_exit_enabled ?? merged.sellTrailingExitEnabled, false),
    sellTrailingExitStages: parseJsonArray(data.sell_trailing_exit_stages ?? merged.sellTrailingExitStages),
    
    longDcaEnabled: parseBoolean(data.long_dca_enabled ?? merged.longDcaEnabled, false),
    longDcaStages: parseJsonArray(data.long_dca_stages ?? merged.longDcaStages),
    buyTakeProfitUseFixedAmount: parseBoolean(data.buy_take_profit_use_fixed_amount ?? merged.buyTakeProfitUseFixedAmount, false),

    shortDcaEnabled: parseBoolean(data.short_dca_enabled ?? merged.shortDcaEnabled, false),
    shortDcaStages: parseJsonArray(data.short_dca_stages ?? merged.shortDcaStages),
    sellTakeProfitUseFixedAmount: parseBoolean(data.sell_take_profit_use_fixed_amount ?? merged.sellTakeProfitUseFixedAmount, false),
  }
}

export function useBotSettings(botId?: string, initialData?: any) {
  const location = useLocation()
  
  const [form, setFormState] = useState<BotForm | null>(() => {
    if (botId && globalFormCache[botId]) return globalFormCache[botId];
    return initialData ? normalizeBotToForm(initialData) : null;
  });
  
  const [loading, setLoading] = useState<boolean>(!!botId && !form)
  const [error, setError] = useState<string | null>(null)
  const [hydrated, setHydrated] = useState<boolean>(!!form)
  
  const fetchedBotIdRef = useRef<string | null>(null);

  const setForm = useCallback((updater: any) => {
    setFormState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      if (botId && next) globalFormCache[botId] = next;
      return next;
    });
  }, [botId]);

  useEffect(() => {
    if (!window) return;
    
    if (!(window as any)._sendChangePatched) {
      const originalSendChange = (window as any).sendChange;
      (window as any).sendChange = (field: string, value: any) => {
        if (originalSendChange && typeof originalSendChange === 'function') {
           Promise.resolve(originalSendChange(field, value)).catch(console.error);
        }
        window.dispatchEvent(new CustomEvent('form-updated', { detail: { field, value } }));
      };
      (window as any)._sendChangePatched = true;
    }

    const listener = (e: any) => {
      const { field, value } = e.detail;
      setFormState(prev => {
        if (!prev) return prev;
        if (prev[field] === value) return prev; 
        const next = { ...prev, [field]: value };
        if (botId) globalFormCache[botId] = next;
        return next;
      });
    };

    window.addEventListener('form-updated', listener);
    return () => window.removeEventListener('form-updated', listener);
  }, [botId]);

  useEffect(() => {
    let mounted = true
    async function load() {
      if (!botId) {
        if (mounted) { setFormState(null); setLoading(false); }
        return;
      }
      if (fetchedBotIdRef.current === botId && globalFormCache[botId]) return;

      const prefetched = (location.state as any)?.prefillBot ?? null
      if (prefetched && prefetched.id === botId) {
        if (mounted) {
          const norm = normalizeBotToForm(prefetched);
          globalFormCache[botId] = norm;
          setFormState(norm)
          setHydrated(true); setLoading(false);
          fetchedBotIdRef.current = botId;
        }
        return
      }

      setLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) return;
        const { data, error } = await supabase.from('bots').select('*').eq('id', botId).eq('user_id', user.id).single()
        if (error) throw error
        if (mounted && data) {
          const norm = normalizeBotToForm(data);
          globalFormCache[botId] = norm;
          setFormState(norm);
          setHydrated(true);
          fetchedBotIdRef.current = botId;
        }
      } catch (e: any) {
        if (mounted) setError(e.message)
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => { mounted = false }
  }, [botId, location.state])

  return { form, setForm, loading, error, hydrated }
}